/**
 * BotSpot Web Dashboard - Credentials Page
 *
 * Main page for managing user credentials with CRUD operations.
 */

import React, { useState } from "react";
import { useCredentials } from "../hooks/useCredentials";
import { CredentialList } from "../components/CredentialList";
import { CredentialForm } from "../components/CredentialForm";
import {
  createCredential,
  updateCredential,
  deleteCredential,
} from "../api/credentials";
import { getErrorMessage } from "../api/client";
import type { Credential } from "../api/types";
import { YELLOW, PIXEL, MONO, buttonPrimary } from "../theme";

/**
 * Form mode type
 */
type FormMode = "create" | "edit" | null;

/**
 * Credentials Page Component
 *
 * Provides a complete interface for managing credentials including:
 * - Viewing all credentials
 * - Creating new credentials
 * - Updating existing credentials
 * - Deleting credentials
 */
export function CredentialsPage() {
  const { credentials, loading, error, refetch } = useCredentials();
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [selectedCredential, setSelectedCredential] =
    useState<Credential | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  /**
   * Handle create button click
   */
  const handleCreate = () => {
    setFormMode("create");
    setSelectedCredential(null);
    setActionError(null);
    setSuccessMessage(null);
  };

  /**
   * Handle edit button click
   */
  const handleEdit = (credential: Credential) => {
    setFormMode("edit");
    setSelectedCredential(credential);
    setActionError(null);
    setSuccessMessage(null);
  };

  /**
   * Handle form cancel
   */
  const handleCancel = () => {
    setFormMode(null);
    setSelectedCredential(null);
    setActionError(null);
  };

  /**
   * Handle form submission (create or update)
   */
  const handleSubmit = async (data: {
    provider: string;
    name: string;
    credential_value: string;
    expires_at?: string;
  }) => {
    setActionLoading(true);
    setActionError(null);
    setSuccessMessage(null);

    try {
      if (formMode === "create") {
        await createCredential(data as any);
        setSuccessMessage("Credential created successfully");
      } else if (formMode === "edit" && selectedCredential) {
        const updateData: any = {
          name: data.name,
        };
        if (data.credential_value) {
          updateData.credential_value = data.credential_value;
        }
        if (data.expires_at) {
          updateData.expires_at = data.expires_at;
        }
        await updateCredential(selectedCredential.id, updateData);
        setSuccessMessage("Credential updated successfully");
      }

      await refetch();
      setFormMode(null);
      setSelectedCredential(null);
    } catch (err) {
      setActionError(
        `Failed to ${formMode === "create" ? "create" : "update"} credential: ${getErrorMessage(err)}`
      );
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle credential deletion
   */
  const handleDelete = async (credentialId: string) => {
    setActionLoading(true);
    setActionError(null);
    setSuccessMessage(null);

    try {
      await deleteCredential(credentialId);
      setSuccessMessage("Credential deleted successfully");
      await refetch();
    } catch (err) {
      setActionError(`Failed to delete credential: ${getErrorMessage(err)}`);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Render loading state
   */
  if (loading && credentials.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}>🔄</div>
          <p style={styles.loadingText}>Loading credentials...</p>
        </div>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error && credentials.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>❌</div>
          <h3 style={styles.errorTitle}>Failed to load credentials</h3>
          <p style={styles.errorMessage}>{error}</p>
          <button style={styles.retryButton} onClick={refetch}>
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  /**
   * Render form view
   */
  if (formMode) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={handleCancel}>
            ← Back to Credentials
          </button>
        </div>

        {actionError && (
          <div style={styles.actionErrorBanner}>
            <span style={styles.actionErrorIcon}>⚠️</span>
            <span style={styles.actionErrorText}>{actionError}</span>
            <button
              style={styles.dismissButton}
              onClick={() => setActionError(null)}
            >
              ✕
            </button>
          </div>
        )}

        <CredentialForm
          credential={selectedCredential || undefined}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={actionLoading}
        />
      </div>
    );
  }

  /**
   * Render main view
   */
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Credentials</h1>
          <p style={styles.subtitle}>
            Manage your secure API keys and authentication tokens
          </p>
        </div>
        <button style={styles.createButton} onClick={handleCreate}>
          ➕ Add Credential
        </button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div style={styles.successBanner}>
          <span style={styles.successIcon}>✓</span>
          <span style={styles.successText}>{successMessage}</span>
          <button
            style={styles.dismissButton}
            onClick={() => setSuccessMessage(null)}
          >
            ✕
          </button>
        </div>
      )}

      {/* Action Error Banner */}
      {actionError && (
        <div style={styles.actionErrorBanner}>
          <span style={styles.actionErrorIcon}>⚠️</span>
          <span style={styles.actionErrorText}>{actionError}</span>
          <button
            style={styles.dismissButton}
            onClick={() => setActionError(null)}
          >
            ✕
          </button>
        </div>
      )}

      {/* Credentials List */}
      {credentials.length === 0 ? (
        <div style={styles.emptyContainer}>
          <div style={styles.emptyIcon}>🔐</div>
          <h3 style={styles.emptyTitle}>No credentials yet</h3>
          <p style={styles.emptyMessage}>
            Get started by adding your first API key or authentication token.
          </p>
          <button style={styles.createButtonLarge} onClick={handleCreate}>
            ➕ Add Your First Credential
          </button>
        </div>
      ) : (
        <CredentialList
          credentials={credentials}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={actionLoading}
        />
      )}
    </div>
  );
}

/**
 * Inline styles
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "2rem",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "2rem",
    flexWrap: "wrap",
    gap: "1rem",
  },
  title: {
    margin: 0,
    fontFamily: PIXEL,
    fontSize: "clamp(0.8rem, 2.5vw, 1.2rem)",
    fontWeight: 400,
    letterSpacing: "0.06em",
    textShadow: "0 0 15px rgba(0,106,167,0.4)",
    color: "rgba(255, 255, 255, 0.87)",
  },
  subtitle: {
    margin: "0.5rem 0 0 0",
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.6)",
    fontFamily: MONO,
    letterSpacing: "0.05em",
  },
  createButton: {
    ...buttonPrimary,
    padding: "0.75rem 1.5rem",
    background: YELLOW,
    border: "none",
    cursor: "pointer",
    fontSize: "0.55rem",
    fontFamily: PIXEL,
    fontWeight: 400,
    color: "#000",
    letterSpacing: "0.1em",
    transition: "all 0.2s",
    boxShadow: "0 0 12px rgba(254,204,0,0.25)",
  },
  createButtonLarge: {
    ...buttonPrimary,
    padding: "0.75rem 1.5rem",
    background: YELLOW,
    border: "none",
    cursor: "pointer",
    fontSize: "0.55rem",
    fontFamily: PIXEL,
    fontWeight: 400,
    color: "#000",
    letterSpacing: "0.1em",
    transition: "all 0.2s",
    boxShadow: "0 0 12px rgba(254,204,0,0.25)",
  },
  backButton: {
    padding: "0.75rem 1.5rem",
    backgroundColor: "transparent",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    cursor: "pointer",
    fontSize: "1rem",
    fontWeight: 500,
    color: "rgba(255, 255, 255, 0.87)",
    transition: "background-color 0.2s",
    fontFamily: MONO,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "4rem 2rem",
  },
  spinner: {
    fontSize: "3rem",
    animation: "spin 1s linear infinite",
    fontFamily: MONO,
  },
  loadingText: {
    marginTop: "1rem",
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.6)",
    fontFamily: MONO,
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "4rem 2rem",
    textAlign: "center",
  },
  errorIcon: {
    fontSize: "3rem",
    marginBottom: "1rem",
  },
  errorTitle: {
    margin: "0 0 0.5rem 0",
    fontSize: "0.8rem",
    fontWeight: 400,
    color: "rgba(255, 255, 255, 0.87)",
    fontFamily: PIXEL,
  },
  errorMessage: {
    margin: "0 0 1.5rem 0",
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.6)",
    maxWidth: "500px",
    fontFamily: MONO,
  },
  retryButton: {
    padding: "0.75rem 1.5rem",
    backgroundColor: "#006AA7",
    border: "none",
    cursor: "pointer",
    fontSize: "1rem",
    fontWeight: 500,
    color: "white",
    transition: "background-color 0.2s",
  },
  successBanner: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    backgroundColor: "rgba(0,201,122,0.1)",
    border: "1px solid rgba(0,201,122,0.3)",
    padding: "1rem",
    marginBottom: "1.5rem",
  },
  successIcon: {
    fontSize: "1.25rem",
  },
  successText: {
    flex: 1,
    fontSize: "0.95rem",
    color: "#00c97a",
  },
  actionErrorBanner: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    padding: "1rem",
    marginBottom: "1.5rem",
  },
  actionErrorIcon: {
    fontSize: "1.25rem",
  },
  actionErrorText: {
    flex: 1,
    fontSize: "0.95rem",
    color: "#fca5a5",
  },
  dismissButton: {
    padding: "0.25rem 0.5rem",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.6)",
    transition: "color 0.2s",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "4rem 2rem",
    textAlign: "center",
  },
  emptyIcon: {
    fontSize: "4rem",
    marginBottom: "1rem",
  },
  emptyTitle: {
    margin: "0 0 0.5rem 0",
    fontSize: "0.8rem",
    fontWeight: 400,
    color: "rgba(255, 255, 255, 0.87)",
    fontFamily: PIXEL,
  },
  emptyMessage: {
    margin: "0 0 1.5rem 0",
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.6)",
    maxWidth: "400px",
    fontFamily: MONO,
  },
};
